// = Libs =
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';

// = Containers & Components =
import Container from '@/common/containers/container/container';
import {
  createQuestion,
  createSection,
  deleteSection,
  getQuestions,
  getSections,
  importQuestions,
  QuestionModel,
  SectionModel,
  updateQuestion,
  updateSection,
} from '@/services/question.service';
import classes from './poll-question.module.scss';
import Button from '@/common/components/ui/buttons/button/button';
import IconButton from '@/common/components/ui/buttons/icon-button/icon-button';
import Question from '@/pages/poll/author-poll/tabs/poll-questions/question/question';
import useDebouncedFunction from '@/helpers/debounce';
import Select from '@/common/components/ui/select/select';
import { OptionModel } from '@/common/components/ui/select/select.types';
import { usePollList } from '@/services/poll.service';

const PollQuestions: FC<{ pollId: number | string }> = (props) => {
  const { pollId } = props;

  const [sections, setSections] = useState<
    {
      sectionId: number;
      sectionTitle: string;
      questions: QuestionModel[];
    }[]
  >([]);

  const [currentInput, setCurrentInput] = useState<number>(-1);
  let [trigger, setTrigger] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      const items = (await getSections(pollId)).data?.items || [];
      const questions = (await getQuestions(pollId)).data?.items || [];
      setSections(
        items.map((section: SectionModel) => {
          return {
            sectionId: section?.sectionId,
            sectionTitle: section?.title,
            questions: questions?.filter(
              (q: QuestionModel) => q?.sectionId === section?.sectionId
            ),
          };
        })
      );
    };
    fetchData();
  }, [pollId, trigger]);

  const handleSubmit = async (submitFunc: () => Promise<any>) => {
    await submitFunc().then(() => setTrigger(trigger + 1));
  };

  const updateSectionTitle = (sectionId: number, title: string) => {
    handleSubmit(() =>
      updateSection(pollId, { sectionId: sectionId, title: title })
    );
  };

  const titleTimerDebounceRef = useRef();

  const handleDeboundTitleEdit = (e: ChangeEvent, sectionId: number) => {
    if (titleTimerDebounceRef.current) {
      clearTimeout(titleTimerDebounceRef.current);
      setCurrentInput(sectionId);
    }
    // @ts-ignore TODO: перевести на useDebouncedFunction
    titleTimerDebounceRef.current = setTimeout(() => {
      // @ts-ignore TODO: Я понятия не имею почему тайпскрипт не дает доступа к value
      updateSectionTitle(sectionId, e.target.value);
      setCurrentInput(-1);
    }, 500);
  };

  const handleDebouncedQuestionEdit = useDebouncedFunction(
    (question: QuestionModel) => {
      handleSubmit(() => updateQuestion(pollId, question));
    },
    500
  );

  const [importFrom, setImportFrom] = useState<number>();
  const polls = usePollList({
    mode: 'author',
    limit: 1000,
    offset: 0,
    mapping: (el) => ({
      value: el.pollId,
      label: el.title,
    }),
  })?.elementList;
  return (
    <Container>
      <div className={classes['section-container']}>
        <div className={classes['monitoring-import']}>
          <Select
            options={polls}
            onChange={(nv) => {
              setImportFrom(Number((nv as OptionModel).value));
            }}
            placeholder={'Импортировать вопросы из мониторинга'}
            value={String(importFrom)}
            isSearchable
            isId
          />
          <Button
            className={classes['import-']}
            color={'primary'}
            onClick={() => {
              handleSubmit(() =>
                importQuestions(Number(importFrom), Number(pollId))
              );
            }}>
            Импортировать
          </Button>
        </div>
        {sections.map((s) => {
          return (
            <div className={classes.section} key={`section-${s.sectionId}`}>
              <div className={classes['section-title']}>
                <input
                  className={classes['section-title-input']}
                  defaultValue={s.sectionTitle}
                  onChange={(e) => handleDeboundTitleEdit(e, s.sectionId)}
                />
                <IconButton
                  iconType={'delete'}
                  color={'secondary'}
                  disabled={s.sectionId === currentInput}
                  onClick={() => {
                    handleSubmit(() => deleteSection(pollId, s.sectionId));
                  }}
                />
              </div>
              <div className={classes['question-container']}>
                {s.questions.map((q) => {
                  return (
                    <Question
                      {...q}
                      pollId={Number(pollId)}
                      handleSubmit={handleDebouncedQuestionEdit}
                      trigger={handleSubmit}
                      key={`question-${q.questionId}`}
                    />
                  );
                })}
              </div>

              <Button
                className={classes['add-button']}
                onClick={() =>
                  handleSubmit(() =>
                    createQuestion(pollId, { sectionId: s.sectionId })
                  )
                }>
                Добавить вопрос
              </Button>
            </div>
          );
        })}
      </div>

      <Button
        className={classes['add-button']}
        onClick={() => {
          handleSubmit(() => createSection(pollId));
        }}>
        Добавить раздел
      </Button>
    </Container>
  );
};
export default PollQuestions;
